import { DNDFieldsEnum } from "./Types";
import { spaTooltipHeaders } from "./TooltipInformation";
import {
  buildSpaRetailSubCategoryOptionsByLevel,
  isSpaRetailSubCategorySampleValue,
  rowContainsSpaRetailSubCategorySample,
} from "./SpaRetailSubCategoriesFields";

export const SPA_CATEGORY_LINKING_FIELD_MAIN_CATEGORY = "Main Category";

export const SPA_CATEGORY_LINKING_SUBCATEGORY_FIELDS = [
  "Subcategory 1",
  "Subcategory 2",
  "Subcategory 3",
  "Subcategory 4",
  "Subcategory 5",
] as const;

export const SPA_CATEGORY_LINKING_DND_CODE =
  SPA_CATEGORY_LINKING_FIELD_MAIN_CATEGORY;
export const SPA_CATEGORY_LINKING_DND_NAME =
  SPA_CATEGORY_LINKING_SUBCATEGORY_FIELDS[0];

export const SPA_CATEGORY_LINKING_PAGE_TITLE = "Category Linking";
export const SPA_CATEGORY_LINKING_MAIN_CATEGORY_LABEL = "Main Category";
export const SPA_CATEGORY_LINKING_MAIN_CATEGORY_PLACEHOLDER =
  "Select from Retail Categories";
export const SPA_CATEGORY_LINKING_LIST_TITLE = "Category Linking Available";
export const SPA_CATEGORY_LINKING_EMPTY_LIST_MESSAGE =
  "No Category Linking Available";
export const SPA_CATEGORY_LINKING_BUTTON_SUFFIX = "Category Link";
export const SPA_CATEGORY_LINKING_SAMPLE_WARNING =
  "Do not use sample data. Those are for demo purposes only!";

export const SPA_CATEGORY_LINKING_HEADER_TOOLTIP =
  spaTooltipHeaders.categoryLinkingHeader ?? "Category Linking";

export const SPA_CATEGORY_LINKING_SUBCATEGORY_TOOLTIPS = [
  spaTooltipHeaders.categoryLinkingSubcategory1,
  spaTooltipHeaders.categoryLinkingSubcategory2,
  spaTooltipHeaders.categoryLinkingSubcategory3,
  spaTooltipHeaders.categoryLinkingSubcategory4,
  spaTooltipHeaders.categoryLinkingSubcategory5,
] as const;

export const SPA_CATEGORY_LINKING_MAIN_CATEGORY_TOOLTIP =
  spaTooltipHeaders.categoryLinkingMainCategory;

export const getSpaCategoryLinkingSubcategoryLabel = (
  fieldLabel: string,
  levelLabel: string,
): string => `${fieldLabel} (${levelLabel})`;

export const getSpaCategoryLinkingSubcategoryPlaceholder = (
  levelLabel: string,
): string => `Select from Retail Sub Categories — ${levelLabel}`;

export const emptySpaCategoryLinkingSubcategories = (): (string | null)[] =>
  SPA_CATEGORY_LINKING_SUBCATEGORY_FIELDS.map(() => null);

export const uniqueSortedSpaCategoryLinkingStrings = (
  values: unknown[],
): string[] => {
  const set = new Set<string>();
  for (const v of values) {
    if (v != null && String(v).trim() !== "") {
      const s = String(v);
      if (isSpaRetailSubCategorySampleValue(s)) {
        continue;
      }
      set.add(s);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
};

export const spaCategoryLinkingOptionsWithValue = (
  base: string[],
  current: string | null | undefined,
): string[] =>
  current &&
  !base.includes(current) &&
  !isSpaRetailSubCategorySampleValue(current)
    ? [...base, current]
    : base;

export const parseSpaCategoryLinkingSubcategoriesFromRow = (
  list: Record<string, string | null | undefined> | null | undefined,
): (string | null)[] =>
  SPA_CATEGORY_LINKING_SUBCATEGORY_FIELDS.map((field) => list?.[field] ?? null);

export const buildSpaCategoryLinkingPayload = (
  mainCategory: string | null,
  subcategories: (string | null)[],
): Record<string, string | null> =>
  SPA_CATEGORY_LINKING_SUBCATEGORY_FIELDS.reduce<Record<string, string | null>>(
    (acc, field, index) => {
      acc[field] = subcategories[index] ?? null;
      return acc;
    },
    { [SPA_CATEGORY_LINKING_FIELD_MAIN_CATEGORY]: mainCategory },
  );

export const buildSpaCategoryLinkingMainCategoryOptions = (
  retailCategories: unknown[] | null | undefined,
): string[] => {
  const rows = Array.isArray(retailCategories) ? retailCategories : [];
  const names = rows
    .filter((r: any) => !rowContainsSpaRetailSubCategorySample(r))
    .map((r: any) => r?.[DNDFieldsEnum.CATEGORYNAME]);
  return uniqueSortedSpaCategoryLinkingStrings(names);
};

export const buildSpaCategoryLinkingSubCategoryOptionsByLevel = (
  retailSubCategories: unknown[] | null | undefined,
): string[][] => {
  const buckets = buildSpaRetailSubCategoryOptionsByLevel(retailSubCategories);
  return buckets.map((values) => uniqueSortedSpaCategoryLinkingStrings(values));
};
